import { supabase } from '../lib/supabase';

export const fatouRefinementService = {
  /**
   * Get knowledge refinement suggestions
   */
  async getRefinements(filters = {}) {
    try {
      let query = supabase?.from('fatou_knowledge_refinements')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.refinementType) {
        query = query?.eq('refinement_type', filters?.refinementType);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching refinements:', error);
      throw error;
    }
  },

  /**
   * Create a new refinement suggestion
   */
  async createRefinement(refinementData) {
    try {
      const { data, error } = await supabase?.from('fatou_knowledge_refinements')?.insert({
          refinement_type: refinementData?.type,
          category: refinementData?.category,
          priority: refinementData?.priority || 'medium',
          title: refinementData?.title,
          current_content: refinementData?.currentContent,
          proposed_content: refinementData?.proposedContent,
          rationale: refinementData?.rationale,
          impact_assessment: refinementData?.impactAssessment,
          source: refinementData?.source || 'admin',
          status: 'pending'
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating refinement:', error);
      throw error;
    }
  },

  /**
   * Update refinement status
   */
  async updateRefinementStatus(refinementId, status, reviewNotes) {
    try {
      const updateData = {
        status,
        review_notes: reviewNotes,
        reviewed_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      if (status === 'approved') {
        updateData.approved_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('fatou_knowledge_refinements')?.update(updateData)?.eq('id', refinementId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating refinement status:', error);
      throw error;
    }
  },

  /**
   * Delete a refinement suggestion
   */
  async deleteRefinement(refinementId) {
    try {
      const { error } = await supabase?.from('fatou_knowledge_refinements')?.delete()?.eq('id', refinementId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting refinement:', error);
      throw error;
    }
  },

  /**
   * Apply approved refinement to Fatou config
   */
  async applyRefinement(refinementId) {
    try {
      // Get the refinement
      const { data: refinement, error: refinementError } = await supabase?.from('fatou_knowledge_refinements')?.select('*')?.eq('id', refinementId)?.single();

      if (refinementError) throw refinementError;

      if (refinement?.status !== 'approved') {
        throw new Error('Le refinement doit être approuvé avant application');
      }

      // Get current Fatou config
      const { data: configData, error: configError } = await supabase?.from('fatou_config')?.select('*')?.eq('is_active', true)?.single();

      if (configError) throw configError;

      let updatedConfig = { ...configData?.config_data };

      // Apply changes based on refinement type
      switch (refinement?.refinement_type) {
        case 'faq_add':
          updatedConfig.faqs = [...(updatedConfig?.faqs || []), refinement?.proposed_content];
          break;

        case 'faq_update':
          const faqIndex = updatedConfig?.faqs?.findIndex(f => f?.id === refinement?.proposed_content?.id);
          if (faqIndex !== -1) {
            updatedConfig.faqs[faqIndex] = refinement?.proposed_content;
          }
          break;

        case 'faq_delete':
          updatedConfig.faqs = updatedConfig?.faqs?.filter(f => f?.id !== refinement?.current_content?.id);
          break;

        case 'knowledge_update':
          updatedConfig.knowledgeBase = {
            ...updatedConfig?.knowledgeBase,
            ...refinement?.proposed_content
          };
          break;

        case 'product_update':
          const productIndex = updatedConfig?.products?.findIndex(p => p?.id === refinement?.proposed_content?.id);
          if (productIndex !== -1) {
            updatedConfig.products[productIndex] = refinement?.proposed_content;
          } else {
            updatedConfig.products = [...(updatedConfig?.products || []), refinement?.proposed_content];
          }
          break;

        case 'rule_update':
          const ruleIndex = updatedConfig?.coherenceRules?.findIndex(r => r?.id === refinement?.proposed_content?.id);
          if (ruleIndex !== -1) {
            updatedConfig.coherenceRules[ruleIndex] = refinement?.proposed_content;
          } else {
            updatedConfig.coherenceRules = [...(updatedConfig?.coherenceRules || []), refinement?.proposed_content];
          }
          break;

        default:
          throw new Error(`Type de refinement non supporté: ${refinement.refinement_type}`);
      }

      // Update Fatou config
      const { error: updateError } = await supabase?.from('fatou_config')?.update({
          config_data: updatedConfig,
          updated_at: new Date()?.toISOString()
        })?.eq('id', configData?.id);

      if (updateError) throw updateError;

      // Mark refinement as implemented
      await supabase?.from('fatou_knowledge_refinements')?.update({
          status: 'implemented',
          updated_at: new Date()?.toISOString()
        })?.eq('id', refinementId);

      return true;
    } catch (error) {
      console.error('Error applying refinement:', error);
      throw error;
    }
  },

  /**
   * Get refinement statistics
   */
  async getRefinementStats() {
    try {
      const { data, error } = await supabase?.from('fatou_knowledge_refinements')?.select('status, priority, refinement_type');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter(r => r?.status === 'pending')?.length || 0,
        approved: data?.filter(r => r?.status === 'approved')?.length || 0,
        implemented: data?.filter(r => r?.status === 'implemented')?.length || 0,
        rejected: data?.filter(r => r?.status === 'rejected')?.length || 0,
        highPriority: data?.filter(r => r?.priority === 'high')?.length || 0,
        byType: {}
      };

      // Count by type
      data?.forEach(r => {
        stats.byType[r.refinement_type] = (stats?.byType?.[r?.refinement_type] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching refinement stats:', error);
      throw error;
    }
  },

  /**
   * Bulk approve refinements
   */
  async bulkApproveRefinements(refinementIds, reviewNotes) {
    try {
      const { data, error } = await supabase?.from('fatou_knowledge_refinements')?.update({
          status: 'approved',
          review_notes: reviewNotes,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.in('id', refinementIds)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk approving refinements:', error);
      throw error;
    }
  }
};

export default fatouRefinementService;